import { Component, OnInit, ViewChild, ViewChildren, QueryList, ViewContainerRef, ComponentFactoryResolver, AfterViewInit, Input, ChangeDetectorRef } from '@angular/core';
import { MatTabGroup } from '@angular/material/tabs';
import { HttpParams } from '@angular/common/http';
import { ProfileViewSelection } from '../shared/interfaces/profile-view-selection.interface';
import { PostLoginService } from '../shared/services/post-login.service';
import { MyProfileComponent } from './my-profile/my-profile.component';
import { PtlWindowLocationService } from '@ptl/global-components';
import { SubmittedApplicationListingComponent } from './submitted-application-listing/submitted-application-listing.component';
import { ApplicationStatusListingComponent } from './application-status-listing/application-status-listing.component';

/**
 * This component reads configurations from the the postLoginService,
 * in order to construct, dynamically, components with configured content
 */
@Component({
    selector: 'app-consumer-profile',
    templateUrl: './consumer-profile.component.html',
    styleUrls: ['./consumer-profile.component.scss'],
    standalone: false
})
export class ConsumerProfileComponent implements OnInit, AfterViewInit {

  /** My Profile Content */
  @Input() myProfileTitle: string;
  @Input() myProfileContent: string;
  @Input() myProfileContentInfo: string;
  @Input() myProfileInfoTitle: string;
  @Input() myProfileIsEditAvailable: boolean;

  /** My Profile Fields to show/hide */
  @Input() showFirstName: "true" | "false" | string;
  @Input() showLastName: "true" | "false" | string;
  @Input() showAddress: "true" | "false" | string;
  @Input() showZipCounty: "true" | "false" | string;
  @Input() showDob: "true" | "false" | string;
  @Input() showGender: "true" | "false" | string;
  @Input() showEmail: "true" | "false" | string;
  @Input() showPhoneNumber: "true" | "false" | string;
  @Input() showPassword: "true" | "false" | string;

  @Input() sectionTitle: string;

  /** Application Status Listing Content */
  @Input() appStatusContentOne: string;
  @Input() appStatusContentTwo: string;
  @Input() appStatusContentThree: string;
  @Input() viewUrl: string;

  /** Application Submitted Listing Content */
  @Input() appSubmittedContentOne: string;
  @Input() appSubmittedContentTwo: string;

  /** Links */
  @Input() planDetailsUrl: string;
  @Input() enrollmentContinueUrl: string;

  /** Consumer Profile Specific */
  @ViewChild('tabGroup', { static: true }) tabGroup: MatTabGroup;
  @ViewChildren('dynamic', {
    read: ViewContainerRef
  }) dynTabs: QueryList<ViewContainerRef>;

  selections: ProfileViewSelection[];

  constructor(
    private postLoginService: PostLoginService,
    private componentFactoryResolver: ComponentFactoryResolver,
    private windowLocationService: PtlWindowLocationService,
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  ngOnInit() {
  }

  ngAfterViewInit() {
    this.postLoginService.sortedRoleSelections$.subscribe((profileViews) => {
      if (profileViews) {
        this.selections = profileViews;

        // Because this page is built with dynamic view container references, we have to force change detection
        // to make sure our dynTabs reference is in sync with our selections before processing our content
        this.changeDetectorRef.detectChanges();

        this.loadSelections();
      }
    });
  }

  loadSelections() {
    this.selections.forEach((page: ProfileViewSelection) => {
      this.dynTabs.forEach((viewContainer: ViewContainerRef, index: number) => {

        /** Populate the pages and provide content if available */
        if (page.order === index) {
          const generatedComponent = this.componentFactoryResolver.resolveComponentFactory(page.componentRef);
          const component = viewContainer.createComponent(generatedComponent);

          /** Specific content handling */
          if (component.instance instanceof MyProfileComponent) {
            component.instance.content = {
              myProfileTitle: this.myProfileTitle,
              myProfileContent: this.myProfileContent,
              myProfileContentInfo: this.myProfileContentInfo,
              myProfileInfoTitle: this.myProfileInfoTitle,
              myProfileIsEditAvailable: this.myProfileIsEditAvailable,
              showFirstName: this.showFirstName,
              showLastName: this.showLastName,
              showAddress: this.showAddress,
              showZipCounty: this.showZipCounty,
              showDob: this.showDob,
              showGender: this.showGender,
              showEmail: this.showEmail,
              showPhoneNumber: this.showPhoneNumber,
              showPassword: this.showPassword,
              sectionTitle: this.sectionTitle
            };
          } else if (component.instance instanceof SubmittedApplicationListingComponent) {
            component.instance.content = {
              planDetailsUrl: this.planDetailsUrl,
              enrollmentContinueUrl: this.enrollmentContinueUrl,
              appSubmittedContentOne: this.appSubmittedContentOne,
              appSubmittedContentTwo: this.appSubmittedContentTwo
            };
          } else if (component.instance instanceof ApplicationStatusListingComponent) {
            component.instance.content = {
              appStatusContentOne: this.appStatusContentOne,
              appStatusContentTwo: this.appStatusContentTwo,
              appStatusContentThree: this.appStatusContentThree
            };
            component.instance.viewUrl = this.viewUrl;
          }
        }
      });
    });

    const queryParams = this.windowLocationService.getParsedQueryParams();
    if (queryParams.hasOwnProperty('myAccountTab')) {
      this.tabGroup.selectedIndex = queryParams['myAccountTab'];
    }
  }

  /**
   * WIP - in case we want to immediately direct the user to one of the other pages
   */
  getParamValueQueryString(paramName) {
    const url = window.location.href;
    let paramValue;
    if (url.includes('?')) {
      const httpParams = new HttpParams({ fromString: url.split('?')[1] });
      paramValue = httpParams.get(paramName);
    }
    return paramValue;
  }
}
